import { RelativePosition } from "@/api/dtos";
import { Button } from "@/components/ui/button.tsx";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui/menubar.tsx";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  Trash2,
} from "lucide-react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

export interface FormHeaderProps {
  title?: string;
  backLabel: string;
  onSave: (status?: "published" | "draft") => void;
  onAddToTrash: () => void;
  linkOverview?: string;
  addToTrashDisabled?: boolean;
  print?: ReactNode;
  publicationStatus?: string;
  relativePosition?: RelativePosition;
}

const FormHeader = ({
  title,
  backLabel,
  onSave,
  onAddToTrash,
  linkOverview,
  addToTrashDisabled,
  print,
  relativePosition,
}: FormHeaderProps) => {
  const navigate = useNavigate();
  const pathname = window.location.pathname.split("/").slice(1);
  const type = pathname[1];

  const location = useLocation();

  const linkBack = `/dashboard/${pathname[1]}`;

  const { t } = useTranslation();

  return (
    <Menubar className="w-full h-[70px] justify-between gap-4 px-6 py-2 bg-white border-b border-slate-300 grid grid-cols-[2fr_5fr_2fr]">
      <div className="flex flex-row justify-start">
        <NavLink
          to={linkBack}
          className="text-[#005d8d]  left-[20px] text-sm font-semibold font-['Raleway'] leading-normal"
        >
          {backLabel}
        </NavLink>
      </div>
      <div className="grid grid-cols-[auto_1fr_auto] place-items-center w-full gap-7">
        <div>
          {relativePosition && relativePosition.previousId && (
            <Link
              className="flex items-center gap-1"
              to={`/edit/${type}/${relativePosition.previousId}${location.search}`}
            >
              <ChevronLeftIcon className="w-3 h-3 text-gray-400" />
              <p className="text-[#006EAB] text-sm font-semibold font-['Raleway']">
                {t(`previous_${type}`)}
              </p>
            </Link>
          )}
        </div>
        <div className="flex gap-3 justify-center items-baseline max-w-full min-w-4">
          <h1 className="truncate w-fit text-[#0C111D] font-semibold text-lg font-['Raleway']">
            {title}
          </h1>
          {relativePosition && (
            <span className="w-fit text-[hsla(222,41%,8%,0.5)] text-xs font-semibold">
              <>
                {relativePosition.position}/{relativePosition.total}
              </>
            </span>
          )}
        </div>
        <div>
          {relativePosition && relativePosition.nextId && (
            <Link
              to={`/edit/${type}/${relativePosition.nextId}${location.search}`}
              className="flex items-center gap-1"
            >
              <p className="text-[#006EAB] text-sm font-semibold font-['Raleway']">
                {t(`next_${type}`)}
              </p>
              <ChevronRightIcon className="w-3 h-3 text-gray-400" />
            </Link>
          )}
        </div>
      </div>
      <div className="justify-end items-center gap-4 flex ">
        <Button
          variant="outline"
          onClick={() => onAddToTrash()}
          disabled={addToTrashDisabled}
        >
          <Trash2 className="w-5 h-5 text-[#DC2626]" />
        </Button>
        {print}
        {linkOverview && (
          <Button
            onClick={() => {
              if (linkOverview) {
                navigate(linkOverview);
              }
            }}
            variant={"outline"}
          >
            <EyeIcon className="w-5 h-5 text-[#64748B] mr-1" />
            <div className="px-0.5 justify-center items-center flex">
              <div className="text-slate-800 text-sm font-semibold font-['Raleway'] leading-normal">
                {t("overview")}
              </div>
            </div>
          </Button>
        )}

        <MenubarMenu>
          <MenubarTrigger className="px-3 py-2 rounded-lg justify-center items-center gap-2 flex bg-primary text-white">
            <p className="text-sm font-semibold font-['Raleway']">
              {t("saveSheet")}
            </p>
            <ChevronDownIcon className="w-5 h-5 " />
          </MenubarTrigger>
          <MenubarContent>
            <MenubarItem onClick={() => onSave("draft")}>
              {t("saveAsDraft")}
            </MenubarItem>
            <MenubarItem onClick={() => onSave("published")}>
              {t("saveAndPublish")}
            </MenubarItem>
          </MenubarContent>
        </MenubarMenu>
      </div>
    </Menubar>
  );
};

export default FormHeader;
